import { useEffect } from "react";

/**
 * Prevents the screen from turning off / screensaver from starting.
 * Uses the Screen Wake Lock API (Chrome 84+, Safari 16.4+, Edge, Firefox 126+).
 * Silently no-ops on browsers without support. Re-acquires the lock when the
 * tab becomes visible again (browsers release it automatically on hide).
 */
export default function useWakeLock() {
  useEffect(() => {
    if (!("wakeLock" in navigator)) return;
    let sentinel = null;
    let cancelled = false;

    const request = async () => {
      try {
        sentinel = await navigator.wakeLock.request("screen");
        sentinel.addEventListener("release", () => {
          // browser released — will be re-acquired on visibilitychange
        });
      } catch {
        // insufficient user activation, low battery, etc. — ignore
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible" && !cancelled) request();
    };

    request();
    document.addEventListener("visibilitychange", onVisibility);
    // Some browsers also require a user gesture — hook the first click as backup
    const onFirstInteract = () => request();
    window.addEventListener("click", onFirstInteract, { once: true });
    window.addEventListener("keydown", onFirstInteract, { once: true });
    window.addEventListener("touchstart", onFirstInteract, { once: true });

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("click", onFirstInteract);
      window.removeEventListener("keydown", onFirstInteract);
      window.removeEventListener("touchstart", onFirstInteract);
      try { sentinel?.release(); } catch { /* ignore */ }
    };
  }, []);
}
